import type { ModelArtifactType } from '../../../types/types';
import type {
  ApiLineage,
  ApiLineageNode,
  LineageLatestTestExecution,
  LineageQueryResponse,
} from '../types';

export const isRestrictedApiLineageNode = (node: { __typename?: string }) =>
  node.__typename === 'RestrictedAsset';

export const mapLatestTestExecutions = (
  latestTestExecutions?: LineageLatestTestExecution[],
) =>
  latestTestExecutions?.map((testExecution) => ({
    testName: testExecution.test.name,
    columnName: testExecution.execution.columnName,
    executionStatus: testExecution.execution.status,
  })) || [];

// All node shapes (API, report, enriched) carry a normalized `type`.
export const isColumnNode = (node?: { type?: ModelArtifactType }) =>
  node?.type === 'column';

const mapAssetNode = (
  node: LineageQueryResponse['lineage']['assets']['nodes'][number],
): ApiLineageNode => ({
  id: node.id,
  isRestricted: isRestrictedApiLineageNode(node),
  type: node.artifactType,
  displayType: node.displayType,
  artifactType: node.artifactType,
  parent: node.level === 'SUB_NODE' ? node.parentId : undefined,
  level: node.level,
  label: node.name,
  tags: node.tags,
  owners: node.owners,
  databaseName: node.dbName,
  schemaName: node.schemaName,
  tableName: node.tableName,
  materialization: node.materialization,
  path: node.path,
  platform: node.platform,
  subType: node.biType || node.semanticType,
  dataPlatform: node.dataPlatform,
  sourceType: node.sourceType,
  columnsCount: node.columnsCount,
  testExecutions: mapLatestTestExecutions(node.latestTestExecutions),
});

const mapColumnNode = (
  node: NonNullable<
    LineageQueryResponse['lineage']['columns']
  >['nodes'][number],
): ApiLineageNode => ({
  id: node.id,
  isRestricted: isRestrictedApiLineageNode(node),
  type: node.artifactType,
  displayType: node.displayType,
  artifactType: node.artifactType,
  parent: node.parentId,
  level: node.level,
  label: node.displayName || node.name,
  tags: node.tags,
  owners: node.owners,
  path: node.path,
});

export const parseLineageResponse = (
  gqlLineageData: LineageQueryResponse | undefined,
): ApiLineage | null => {
  if (!gqlLineageData?.lineage?.assets) return null;

  const nodes: ApiLineageNode[] = gqlLineageData.lineage.assets.nodes
    .filter((node) => node.level !== 'COLLECTION')
    .map(mapAssetNode);

  const columnNodes: ApiLineageNode[] =
    gqlLineageData.lineage.columns?.nodes.map(mapColumnNode) || [];

  const edges: Array<[string, string]> =
    gqlLineageData.lineage.assets.edges.map((edge) => [edge.dst, edge.src]);

  const columnEdges: Array<[string, string]> =
    gqlLineageData.lineage.columns?.edges.map((edge) => [edge.dst, edge.src]) ||
    [];

  return {
    nodes: [...nodes, ...columnNodes],
    edges: [...edges, ...columnEdges],
  };
};
